/**
 * Local Storage Management for Groups
 * 
 * Handles persistence of group data in localStorage.
 * Following dev guide: deterministic, versioned schema, migration support.
 */

import { Group, SCHEMA_VERSION } from './groupModel';

// Storage keys
const GROUPS_KEY = 'expense-splitter-groups';
const CURRENT_GROUP_KEY = 'expense-splitter-current-group';

/**
 * Storage wrapper with error handling
 */
class GroupStorage {
  /**
   * Save a group to localStorage
   */
  saveGroup(group: Group): void {
    try {
      const groups = this.getAllGroups();
      groups[group.id] = {
        ...group,
        updatedAt: new Date().toISOString(),
        schemaVersion: SCHEMA_VERSION
      };
      
      localStorage.setItem(GROUPS_KEY, JSON.stringify(groups));
    } catch (error) {
      console.error('Failed to save group:', error);
      throw new Error('Storage quota exceeded or localStorage blocked');
    }
  }

  /**
   * Load a specific group by ID
   */
  loadGroup(groupId: string): Group | null {
    try {
      const groups = this.getAllGroups();
      const group = groups[groupId];
      
      if (!group) {
        return null;
      }

      // Basic schema migration (future-proofing)
      if (group.schemaVersion !== SCHEMA_VERSION) {
        console.warn(`Group ${groupId} has outdated schema version ${group.schemaVersion}`);
        // Migration logic would go here in the future
      }

      return group;
    } catch (error) {
      console.error('Failed to load group:', error);
      return null;
    }
  }

  /**
   * Get all groups from localStorage
   */
  getAllGroups(): Record<string, Group> {
    try {
      const stored = localStorage.getItem(GROUPS_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      console.error('Failed to load groups:', error);
      return {};
    }
  }

  /**
   * Delete a group
   */
  deleteGroup(groupId: string): void {
    try {
      const groups = this.getAllGroups();
      delete groups[groupId];
      localStorage.setItem(GROUPS_KEY, JSON.stringify(groups));

      // Clear current group if it's the one being deleted
      if (this.getCurrentGroupId() === groupId) {
        this.clearCurrentGroup();
      }
    } catch (error) {
      console.error('Failed to delete group:', error);
    }
  }

  /**
   * Set the current active group ID
   */
  setCurrentGroup(groupId: string): void {
    try {
      localStorage.setItem(CURRENT_GROUP_KEY, groupId);
    } catch (error) {
      console.error('Failed to set current group:', error);
    }
  }

  /**
   * Get the current active group ID
   */
  getCurrentGroupId(): string | null {
    try {
      return localStorage.getItem(CURRENT_GROUP_KEY);
    } catch (error) {
      console.error('Failed to get current group:', error);
      return null;
    }
  }

  /**
   * Clear the current group
   */
  clearCurrentGroup(): void {
    try {
      localStorage.removeItem(CURRENT_GROUP_KEY);
    } catch (error) {
      console.error('Failed to clear current group:', error);
    }
  }

  /**
   * Get recent groups (last 5)
   */
  getRecentGroups(): Group[] {
    try {
      const groups = this.getAllGroups();
      return Object.values(groups)
        .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
        .slice(0, 5);
    } catch (error) {
      console.error('Failed to get recent groups:', error);
      return [];
    }
  }

  /**
   * Check if localStorage is available and has space
   */
  checkStorageHealth(): { available: boolean; quotaExceeded: boolean } {
    try {
      const testKey = 'expense-splitter-storage-test';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      return { available: true, quotaExceeded: false };
    } catch (error) {
      if (error instanceof Error && error.name === 'QuotaExceededError') {
        return { available: true, quotaExceeded: true };
      }
      return { available: false, quotaExceeded: false };
    }
  }
}

// Export singleton instance
export const groupStorage = new GroupStorage();
